/**
 * Module dependencies.
 */

import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Home } from 'app/components/pages/home/home';
import { Landing } from 'app/components/pages/landing/landing';
import { CookiePage, PrivacyPage, SecurityPage, TermsPage } from 'app/components/pages/legal/legal';
import { StatusPage } from 'app/components/pages/status/status';
import { FaqsPage } from 'app/components/pages/faqs/faqs';
import { RegisterPage } from 'app/components/pages/auth/register';
import { PublicMovementShare } from 'app/components/pages/share/public-movement-share';
import { VerifyPage } from 'app/components/pages/auth/verify';
import { ForgotPasswordPage } from 'app/components/pages/auth/forgot-password';
import { ResetPasswordPage } from 'app/components/pages/auth/reset-password';
import { appRoutes } from 'app/routes';
import { useFeature } from 'app/hooks/requests/use-feature-flags';
import React, { useEffect, useMemo } from 'react';
import { SkinDashboard } from './components/templates/dashboard/skin-dashboard';
import { DashboardOverview } from './components/pages/dashboard/overview/dashboard-overview';
import { MovementsOverview } from './components/pages/bank-accounts/overview/movements-overview';
import { SkinSettings } from './components/templates/dashboard/skin-settings';
import { SettingsProfile } from './components/pages/settings/profile/settings-profile';
import { SettingsBackups } from './components/pages/settings/backups/settings-backups';
import { SettingsNotifications } from './components/pages/settings/notifications/settings-notifications';
import { useTranslation } from 'react-i18next';
import { listCrudRoutes } from './components/organisms/crud/utils';
import { createBudgetsTemplate } from './components/pages/bank-accounts/budgets/form-config';
import { createCategoriesTemplate } from './components/pages/bank-accounts/categories/form-config';
import { createSubCategoriesTemplate } from './components/pages/bank-accounts/sub-categories/form-config';
import { createBankAccountsTemplate } from './components/pages/bank-accounts/bank-accounts/form-config';
import { BankAccountConnect } from './components/pages/bank-accounts/bank-accounts/bank-account-connect';
import { BankAccountSync } from './components/pages/bank-accounts/bank-accounts/bank-account-connect/bank-account-sync';
import { createTransactionsTemplate } from './components/pages/bank-accounts/movements/movements-config';
import { SkinProjects } from './components/templates/dashboard/skin-projects';
import { ProjectsOverview } from './components/pages/projects/overview/projects-overview';
import { AddNewProject } from './components/pages/projects/overview/add-new-project';
import { Project } from './components/pages/projects/project/project';
import { ProjectSettings } from './components/pages/projects/project/settings/project-settings';
import { createProjectSuppliersTemplate } from './components/pages/projects/project/suppliers/form-config';
import { createProjectSuppliersContactsTemplate } from './components/pages/projects/project/suppliers/form-contacts-config';
import { createProjectBudgetsTemplate } from './components/pages/projects/project/budgets/form-config';
import { createProjectProposalsTemplate } from './components/pages/projects/project/proposals/form-config';
import { movementsCrudRoutes } from './components/pages/projects/project/movements/movements-crud-routes';
import { createProjectPaymentTypesTemplate } from './components/pages/projects/project/payment-types/form-config';
import { SkinBankAccounts } from './components/templates/dashboard/skin-bank-accounts';
import { createExtendedProposalsTemplate } from './components/pages/projects/project/budgets/form-proposals-config';
import { SkinAppsCrypto } from './components/templates/dashboard/skin-apps-crypto';
import { AppsCryptoOverview } from './components/pages/apps-crypto/overview/apps-crypto-overview';
import { createAppsCryptoProvidersTemplate } from './components/pages/apps-crypto/providers/form-config';
import { createAppsCryptoTransactionsTemplate } from './components/pages/apps-crypto/transactions/form-config';
import { TradesList } from './components/pages/apps-crypto/trades/list/trades-list';
import { DashboardHeader } from './components/templates/dashboard/dashboard-header';
import { SkinNotes } from './components/templates/dashboard/skin-notes';
import { Notes } from './components/pages/notes/notes';
import { SkinCryptoGest } from './components/templates/dashboard/skin-crypto-gest';
import { SkinPoolInvest } from './components/templates/dashboard/skin-pool-invest';
import { PoolInvestProjects } from './components/pages/pool-invest/projects/pool-invest-projects';
import { PoolInvestProjectDetail } from './components/pages/pool-invest/project/pool-invest-project-detail';
import { createPoolInvestUsersTemplate } from './components/pages/pool-invest/project/users/form-config';
import { createPoolInvestRulesTemplate } from './components/pages/pool-invest/project/rules/form-config';
import { createPoolInvestCategoriesTemplate } from './components/pages/pool-invest/project/categories/form-config';
import { createPoolInvestTransactionsTemplate } from './components/pages/pool-invest/project/transactions/form-config';
import { CryptoGestOverview } from './components/pages/crypto-gest/overview/crypto-gest-overview';
import { CryptoGestWallets } from './components/pages/crypto-gest/wallets/crypto-gest-wallets';
import { CryptoGestWalletDetail } from './components/pages/crypto-gest/wallets/crypto-gest-wallet-detail';
import { CryptoGestTransactions } from './components/pages/crypto-gest/transactions/crypto-gest-transactions';
import { CryptoGestSettings } from './components/pages/crypto-gest/settings/crypto-gest-settings';
import { CryptoGestAssetDetail } from './components/pages/crypto-gest/assets/crypto-gest-asset-detail';
import { CryptoGestLoans } from './components/pages/crypto-gest/loans/crypto-gest-loans';
import { SkinCryptoBot } from './components/templates/dashboard/skin-crypto-bot';
import { CryptoBotOverview } from './components/pages/crypto-bot/overview/crypto-bot-overview';
import { CryptoBotPositions } from './components/pages/crypto-bot/positions/crypto-bot-positions';
import { CryptoBotRebalances } from './components/pages/crypto-bot/rebalances/crypto-bot-rebalances';
import { CryptoBotLogs } from './components/pages/crypto-bot/logs/crypto-bot-logs';
import { CryptoBotSettings } from './components/pages/crypto-bot/settings/crypto-bot-settings';
import { SkinAdministration } from './components/templates/dashboard/skin-administration';
import { AdministrationList } from './components/pages/administration/administration-list';
import { AdministrationUser } from './components/pages/administration/administration-user';
import { AdministrationFeatureFlags } from './components/pages/administration/administration-feature-flags';
import { AdministrationStablecoins } from './components/pages/administration/administration-stablecoins';
import { AdministrationDevelopment } from './components/pages/administration/administration-development';
import { FeatureGuard } from './components/templates/dashboard/feature-guard';

/**
 * Scroll to the top on every route change.
 */

function ScrollToTop(): null {
  const { hash, pathname } = useLocation();

  useEffect(() => {
    // When a hash is present (e.g. /#features), scroll to that element instead
    // of the top — this makes anchor links work across pages.
    if (hash) {
      const element = document.getElementById(hash.slice(1));

      if (element) {
        element.scrollIntoView();

        return;
      }
    }

    window.scrollTo(0, 0);
  }, [hash, pathname]);

  return null;
}

/**
 * Export `AppRoutes` component.
 */

export function AppRoutes(): JSX.Element {
  const [translate] = useTranslation();
  const badgesEnabled = useFeature('transactionBadges');
  const budgetsTemplate = useMemo(() => createBudgetsTemplate(translate), [translate]);
  const categoriesTemplate = useMemo(() => createCategoriesTemplate(translate), [translate]);
  const subCategoriesTemplate = useMemo(() => createSubCategoriesTemplate(translate), [translate]);
  const bankAccountsTemplate = useMemo(() => createBankAccountsTemplate(translate), [translate]);
  const transactionsTemplate = useMemo(
    () => createTransactionsTemplate(translate, badgesEnabled),
    [translate, badgesEnabled]
  );
  const projectSuppliersContactsTemplate = useMemo(
    () => createProjectSuppliersContactsTemplate(translate),
    [translate]
  );

  const projectSuppliersTemplate = useMemo(
    () => createProjectSuppliersTemplate(translate, projectSuppliersContactsTemplate),
    [projectSuppliersContactsTemplate, translate]
  );

  const projectProposalsTemplate = useMemo(() => createProjectProposalsTemplate(translate), [translate]);

  const extendedProposalsTemplate = useMemo(() => {
    return createExtendedProposalsTemplate(translate);
  }, [translate]);

  const projectBudgetsTemplate = useMemo(
    () => createProjectBudgetsTemplate(translate, extendedProposalsTemplate),
    [extendedProposalsTemplate, translate]
  );

  const projectPaymentTypesTemplate = useMemo(() => createProjectPaymentTypesTemplate(translate), [translate]);

  // Pool Invest
  const poolInvestUsersTemplate = useMemo(() => createPoolInvestUsersTemplate(translate), [translate]);
  const poolInvestRulesTemplate = useMemo(() => createPoolInvestRulesTemplate(translate), [translate]);
  const poolInvestCategoriesTemplate = useMemo(() => createPoolInvestCategoriesTemplate(translate), [translate]);
  const poolInvestTransactionsTemplate = useMemo(() => createPoolInvestTransactionsTemplate(translate), [translate]);

  // Apps Crypto
  const appsCryptoProvidersTemplate = useMemo(() => createAppsCryptoProvidersTemplate(translate), [translate]);
  const appsCryptoTransactionsTemplate = useMemo(() => createAppsCryptoTransactionsTemplate(translate), [translate]);

  return (
    <>
      <ScrollToTop />

      <Routes>
        <Route Component={() => <Landing />} path={appRoutes.home} />
        <Route Component={() => <Home />} path={appRoutes.login} />
        <Route Component={() => <RegisterPage />} path={appRoutes.register} />
        <Route Component={() => <VerifyPage />} path={appRoutes.verify} />
        <Route Component={() => <ForgotPasswordPage />} path={appRoutes.forgotPassword} />
        <Route Component={() => <ResetPasswordPage />} path={appRoutes.resetPassword} />
        <Route Component={() => <TermsPage />} path={appRoutes.legal.terms} />
        <Route Component={() => <PrivacyPage />} path={appRoutes.legal.privacy} />
        <Route Component={() => <CookiePage />} path={appRoutes.legal.cookies} />
        <Route Component={() => <SecurityPage />} path={appRoutes.legal.security} />
        <Route Component={() => <StatusPage />} path={appRoutes.status} />
        <Route Component={() => <FaqsPage />} path={appRoutes.faqs} />
        <Route Component={() => <PublicMovementShare />} path={appRoutes.share.movements} />

        <Route Component={() => <DashboardHeader />} path={appRoutes.dashboard.base}>
          <Route Component={() => <SkinDashboard />} path={appRoutes.dashboard.home.base}>
            <Route Component={() => <DashboardOverview />} index />
          </Route>

          <Route Component={() => <SkinBankAccounts />} path={appRoutes.dashboard.bankAccounts.base}>
            <Route Component={() => <MovementsOverview />} index />
            <Route Component={() => <BankAccountConnect />} path={appRoutes.dashboard.bankAccounts.accounts.connect} />
            <Route Component={() => <BankAccountSync />} path={appRoutes.dashboard.bankAccounts.accounts.sync} />

            {listCrudRoutes(bankAccountsTemplate)}

            {listCrudRoutes(categoriesTemplate)}

            {listCrudRoutes(subCategoriesTemplate)}

            {listCrudRoutes(budgetsTemplate)}

            {listCrudRoutes(transactionsTemplate)}
          </Route>

          <Route
            Component={() => (
              <FeatureGuard feature={'projects'}>
                <SkinProjects />
              </FeatureGuard>
            )}
            path={appRoutes.dashboard.projects.base}
          >
            <Route Component={() => <ProjectsOverview />} index />
            <Route Component={() => <AddNewProject />} path={appRoutes.dashboard.projects.addNewProject} />
            <Route Component={() => <Project />} path={appRoutes.dashboard.projects.project.overview} />

            {listCrudRoutes(projectSuppliersTemplate)}

            {listCrudRoutes(projectSuppliersContactsTemplate)}

            {listCrudRoutes(projectBudgetsTemplate)}

            {listCrudRoutes(projectProposalsTemplate)}

            {movementsCrudRoutes()}

            {listCrudRoutes(projectPaymentTypesTemplate)}

            <Route Component={() => <ProjectSettings />} path={appRoutes.dashboard.projects.project.settings} />
          </Route>

          <Route
            Component={() => (
              <FeatureGuard feature={'cryptoManager'}>
                <SkinAppsCrypto />
              </FeatureGuard>
            )}
            path={appRoutes.dashboard.appsCrypto.base}
          >
            <Route Component={() => <AppsCryptoOverview />} index />

            {listCrudRoutes(appsCryptoProvidersTemplate)}

            {listCrudRoutes(appsCryptoTransactionsTemplate)}

            <Route Component={() => <TradesList />} path={appRoutes.dashboard.appsCrypto.trades} />
          </Route>

          <Route
            Component={() => (
              <FeatureGuard feature={'notes'}>
                <SkinNotes />
              </FeatureGuard>
            )}
            path={appRoutes.dashboard.notes.base}
          >
            <Route Component={() => <Notes />} index />
          </Route>

          <Route
            Component={() => (
              <FeatureGuard feature={'poolInvest'}>
                <SkinPoolInvest />
              </FeatureGuard>
            )}
            path={appRoutes.dashboard.poolInvest.base}
          >
            <Route Component={() => <PoolInvestProjects />} index />
            <Route Component={() => <PoolInvestProjectDetail />} path={appRoutes.dashboard.poolInvest.project} />

            {listCrudRoutes(poolInvestUsersTemplate)}

            {listCrudRoutes(poolInvestRulesTemplate)}

            {listCrudRoutes(poolInvestCategoriesTemplate)}

            {listCrudRoutes(poolInvestTransactionsTemplate)}
          </Route>

          <Route
            Component={() => (
              <FeatureGuard feature={'cryptoBot'}>
                <SkinCryptoBot />
              </FeatureGuard>
            )}
            path={appRoutes.dashboard.cryptoBot.base}
          >
            <Route Component={() => <CryptoBotOverview />} index />
            <Route Component={() => <CryptoBotPositions />} path={appRoutes.dashboard.cryptoBot.positions} />
            <Route Component={() => <CryptoBotRebalances />} path={appRoutes.dashboard.cryptoBot.rebalances} />
            <Route Component={() => <CryptoBotLogs />} path={appRoutes.dashboard.cryptoBot.logs} />
            <Route Component={() => <CryptoBotSettings />} path={appRoutes.dashboard.cryptoBot.settings} />
          </Route>

          <Route
            Component={() => (
              <FeatureGuard feature={'cryptoGest'}>
                <SkinCryptoGest />
              </FeatureGuard>
            )}
            path={appRoutes.dashboard.cryptoGest.base}
          >
            <Route Component={() => <CryptoGestOverview />} index />
            <Route Component={() => <CryptoGestWallets />} path={appRoutes.dashboard.cryptoGest.wallets} />
            <Route Component={() => <CryptoGestWalletDetail />} path={appRoutes.dashboard.cryptoGest.wallet} />
            <Route Component={() => <CryptoGestTransactions />} path={appRoutes.dashboard.cryptoGest.transactions} />
            <Route Component={() => <CryptoGestLoans />} path={appRoutes.dashboard.cryptoGest.loans} />
            <Route Component={() => <CryptoGestSettings />} path={appRoutes.dashboard.cryptoGest.settings} />
            <Route Component={() => <CryptoGestAssetDetail />} path={appRoutes.dashboard.cryptoGest.asset} />
          </Route>

          <Route Component={() => <SkinAdministration />} path={appRoutes.dashboard.administration.base}>
            <Route Component={() => <AdministrationList />} index />
            <Route
              Component={() => <AdministrationFeatureFlags />}
              path={appRoutes.dashboard.administration.featureFlags}
            />
            <Route
              Component={() => <AdministrationStablecoins />}
              path={appRoutes.dashboard.administration.stablecoins}
            />
            <Route
              Component={() => <AdministrationDevelopment />}
              path={appRoutes.dashboard.administration.development}
            />
            <Route Component={() => <AdministrationUser />} path={appRoutes.dashboard.administration.user} />
          </Route>

          <Route Component={() => <SkinSettings />} path={appRoutes.dashboard.settings.base}>
            <Route Component={() => <Navigate to={appRoutes.dashboard.settings.profile} />} index />
            <Route Component={() => <SettingsProfile />} path={appRoutes.dashboard.settings.profile} />
            <Route Component={() => <SettingsBackups />} path={appRoutes.dashboard.settings.backups} />
            <Route Component={() => <SettingsNotifications />} path={appRoutes.dashboard.settings.notifications} />
          </Route>
        </Route>

        <Route Component={() => <Navigate to={appRoutes.home} />} path={'*'} />
      </Routes>
    </>
  );
}
